import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { beaaAvatarImage } from '@/mocks/beaa';
import { beaaPortalProfile, matchBeaaAnswer } from '@/utils/beaaAssistant';
import type {
  BeaaAssistantAction,
  BeaaAssistantEntry,
  BeaaAssistantProfile,
} from '@/utils/beaaAssistant';
import { playBeaaMessageSound, unlockBeaaAudio } from '@/utils/beaaChatSound';
import type { SecaoManualAtiva } from '@/utils/beaaContextoSecao';

const SOUND_PREF_KEY = 'beaa-chat-sound';

type ChatMessage = {
  id: string;
  from: 'beaa' | 'user';
  text: string;
  actions?: BeaaAssistantAction[];
};

type BeaaAssistantPanelProps = {
  onClose: () => void;
  /** Perfil de conteúdo da Beaa (portal do cidadão ou Console Administrativo). */
  profile?: BeaaAssistantProfile;
  /**
   * Resolvedor assíncrono opcional. Quando informado, é ele quem decide a
   * resposta — usado no Console para somar os artigos de manual vindos do banco.
   */
  resolver?: (pergunta: string) => Promise<BeaaAssistantEntry>;
  /** Pergunta que já abre a conversa (ex.: vinda de um botão "Perguntar à Beaa"). */
  perguntaInicial?: string | null;
  /** Seção do manual visível quando o chat foi aberto — contextualiza a Beaa. */
  contextoSecao?: SecaoManualAtiva | null;
};

/**
 * Painel de conversa da Beaa no estilo do site.
 * Substitui o widget genérico do assistente por uma janela própria,
 * em português, com o avatar da Beaa e as cores da marca.
 * As respostas vêm de um guia local (sem IA) e podem ser trocadas por IA depois.
 */
export default function BeaaAssistantPanel({
  onClose,
  profile = beaaPortalProfile,
  resolver,
  perguntaInicial,
  contextoSecao,
}: BeaaAssistantPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    { id: 'greeting', from: 'beaa', text: profile.greeting, actions: [] },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem(SOUND_PREF_KEY);
      return stored === null ? true : stored === '1';
    } catch {
      return true;
    }
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<number[]>([]);
  const idRef = useRef(0);

  // Limpa os timers pendentes ao desmontar.
  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
    },
    [],
  );

  // O navegador só libera áudio após uma interação do usuário.
  useEffect(() => {
    const unlock = () => unlockBeaaAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  // Fecha com a tecla Esc.
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Se o perfil (ex.: o nome do operador) chegar depois do painel aberto,
  // atualiza a saudação enquanto a conversa ainda não começou.
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0].id !== 'greeting') return prev;
      if (prev[0].text === profile.greeting) return prev;
      return [{ ...prev[0], text: profile.greeting }];
    });
  }, [profile.greeting]);

  // Mantém a conversa sempre rolada para o fim.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isTyping]);

  const showSuggestions = useMemo(
    () => !messages.some((message) => message.from === 'user'),
    [messages],
  );

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SOUND_PREF_KEY, next ? '1' : '0');
      } catch {
        /* ignora falha de armazenamento */
      }
      if (next) unlockBeaaAudio();
      return next;
    });
  };

  const sendMessage = (raw: string) => {
    const text = raw.trim();
    if (!text) return;

    idRef.current += 1;
    setMessages((prev) => [...prev, { id: `u-${idRef.current}`, from: 'user', text }]);
    setInput('');
    setIsTyping(true);
    setNotice(null);

    const timer = window.setTimeout(async () => {
      let reply: BeaaAssistantEntry;
      try {
        reply = resolver ? await resolver(text) : matchBeaaAnswer(text, profile);
      } catch {
        reply = profile.fallback;
      }
      setIsTyping(false);
      idRef.current += 1;
      setMessages((prev) => [
        ...prev,
        { id: `b-${idRef.current}`, from: 'beaa', text: reply.answer, actions: reply.actions },
      ]);
      if (soundOn) playBeaaMessageSound();
    }, 850 + Math.random() * 650);

    timersRef.current.push(timer);
  };

  // Abre a conversa já com uma pergunta (ex.: "Perguntar à Beaa" de uma seção do manual).
  const perguntaInicialEnviada = useRef(false);
  useEffect(() => {
    if (!perguntaInicial || perguntaInicialEnviada.current) return;
    perguntaInicialEnviada.current = true;
    const timer = window.setTimeout(() => sendMessage(perguntaInicial), 350);
    timersRef.current.push(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perguntaInicial]);

  // Se o chat foi aberto estando numa seção do manual, a Beaa já se contextualiza.
  const contextoInicialEnviado = useRef(false);
  useEffect(() => {
    if (!contextoSecao || contextoInicialEnviado.current) return;
    contextoInicialEnviado.current = true;
    idRef.current += 1;
    setMessages((prev) => [
      ...prev,
      {
        id: `ctx-${idRef.current}`,
        from: 'beaa',
        text: `Vi que você está na seção "${contextoSecao.titulo}" do manual. Posso explicar esse passo a passo — é só perguntar aqui embaixo — ou te levar direto até o trecho.`,
        actions: [
          {
            label: 'Ir para esta seção',
            icon: 'ri-book-open-line',
            to: `${contextoSecao.rota}#${contextoSecao.id}`,
          },
        ],
      },
    ]);
  }, [contextoSecao]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendMessage(input);
  };

  const handleMic = () => {
    unlockBeaaAudio();
    setNotice('A conversa por voz chega em breve. Por enquanto, é só escrever aqui embaixo.');
    const timer = window.setTimeout(() => setNotice(null), 4000);
    timersRef.current.push(timer);
  };

  return (
    <div
      role="dialog"
      aria-label={profile.ariaLabel}
      className="animate-panel-in fixed bottom-24 right-5 z-[60] flex h-[34rem] max-h-[calc(100vh-8rem)] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 shadow-warm"
    >
      {/* Cabeçalho */}
      <div className="relative flex shrink-0 items-center gap-3 bg-primary-500 px-4 py-3.5">
        <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full border-2 border-background-50">
          <img
            src={beaaAvatarImage}
            alt="Beaa"
            className="h-full w-full object-cover object-center"
          />
          <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-primary-500 bg-accent-400" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-heading font-bold leading-tight text-background-50">Beaa</p>
          <p className="text-[11px] leading-tight text-background-50/85">
            {isTyping ? 'digitando...' : profile.headerSubtitle}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={toggleSound}
            aria-pressed={soundOn}
            aria-label={soundOn ? 'Desativar som das mensagens' : 'Ativar som das mensagens'}
            title={soundOn ? 'Som das mensagens ligado' : 'Som das mensagens desligado'}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-background-50/90 transition-colors hover:bg-background-50/15"
          >
            <i className={soundOn ? 'ri-volume-up-line' : 'ri-volume-mute-line'} />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar conversa"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-background-50/90 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      </div>

      {/* Módulo atual */}
      {profile.contextoModulo &&
        (profile.contextoModulo.to ? (
          <Link
            to={profile.contextoModulo.to}
            onClick={onClose}
            className="flex shrink-0 cursor-pointer items-center gap-2 border-b border-background-200/70 bg-accent-100 px-4 py-2 transition-colors hover:bg-accent-200"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className={`${profile.contextoModulo.icon} text-sm`} />
            </span>
            <span className="min-w-0 text-xs leading-tight text-accent-900">
              Você está em:{' '}
              <strong className="font-semibold">{profile.contextoModulo.label}</strong>
            </span>
            <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-accent-800">
              Abrir tela
              <i className="ri-arrow-right-s-line" />
            </span>
          </Link>
        ) : (
          <div className="flex shrink-0 items-center gap-2 border-b border-background-200/70 bg-accent-100 px-4 py-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className={`${profile.contextoModulo.icon} text-sm`} />
            </span>
            <span className="min-w-0 text-xs leading-tight text-accent-900">
              Você está em:{' '}
              <strong className="font-semibold">{profile.contextoModulo.label}</strong>
            </span>
          </div>
        ))}

      {/* Mensagens */}
      <div
        ref={scrollRef}
        aria-live="polite"
        className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-background-100 px-4 py-4"
      >
        {messages.map((message) =>
          message.from === 'beaa' ? (
            <div key={message.id} className="animate-chat-bubble flex items-start gap-2">
              <span className="h-7 w-7 shrink-0 overflow-hidden rounded-full border border-background-200/70">
                <img
                  src={beaaAvatarImage}
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              </span>
              <div className="min-w-0 max-w-[85%]">
                <p className="rounded-2xl rounded-tl-sm border border-background-200/70 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-800">
                  {message.text}
                </p>
                {message.actions && message.actions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {message.actions.map((action) =>
                      action.href ? (
                        <a
                          key={action.label}
                          href={action.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                        >
                          <i className={action.icon} />
                          {action.label}
                        </a>
                      ) : (
                        <Link
                          key={action.label}
                          to={action.to ?? '/'}
                          onClick={onClose}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                        >
                          <i className={action.icon} />
                          {action.label}
                        </Link>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div key={message.id} className="animate-chat-bubble flex justify-end">
              <p className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary-500 px-3.5 py-2.5 text-sm leading-relaxed text-background-50">
                {message.text}
              </p>
            </div>
          ),
        )}

        {isTyping && (
          <div className="animate-chat-bubble flex items-start gap-2">
            <span className="h-7 w-7 shrink-0 overflow-hidden rounded-full border border-background-200/70">
              <img
                src={beaaAvatarImage}
                alt=""
                className="h-full w-full object-cover object-center"
              />
            </span>
            <div className="inline-flex items-center gap-2.5 rounded-2xl rounded-tl-sm border border-background-200/70 bg-background-50 px-3.5 py-2.5">
              <span className="flex items-center gap-1">
                <span className="typing-dot h-1.5 w-1.5 rounded-full bg-primary-400" />
                <span
                  className="typing-dot h-1.5 w-1.5 rounded-full bg-primary-400"
                  style={{ animationDelay: '0.15s' }}
                />
                <span
                  className="typing-dot h-1.5 w-1.5 rounded-full bg-primary-400"
                  style={{ animationDelay: '0.3s' }}
                />
              </span>
              <span className="text-xs text-foreground-600">Beaa está digitando...</span>
            </div>
          </div>
        )}
      </div>

      {/* Rodapé */}
      <div className="shrink-0 border-t border-background-200/70 bg-background-50 px-4 pb-4 pt-3">
        {showSuggestions && (
          <div className="mb-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
              Perguntas frequentes
            </p>
            <div className="flex flex-wrap gap-2">
              {profile.suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  onClick={() => sendMessage(suggestion.label)}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-background-200/70 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={suggestion.icon} />
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {notice && (
          <p className="mb-2 flex items-start gap-1.5 rounded-xl bg-accent-100 px-3 py-2 text-[11px] leading-snug text-accent-900">
            <i className="ri-information-line mt-0.5 shrink-0" />
            {notice}
          </p>
        )}

        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <div className="flex flex-1 items-center rounded-full border border-background-200/70 bg-background-50 px-3.5 py-2 transition-colors focus-within:border-primary-300">
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Escreva sua mensagem..."
              aria-label="Escreva sua mensagem para a Beaa"
              className="w-full min-w-0 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            disabled={!input.trim()}
            aria-label="Enviar mensagem"
            className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-send-plane-fill text-sm" />
          </button>
          <button
            type="button"
            onClick={handleMic}
            aria-label="Conversa por voz (em breve)"
            title="Conversa por voz (em breve)"
            className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-200/70 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-mic-line" />
          </button>
        </form>
      </div>
    </div>
  );
}